// =============================================================================
// CheckinFlowModal — shared check-in flow for QR scan + manual button
// =============================================================================
// Drives both the ScanScreen camera flow and the per-row CheckinButton:
//
//   1. Calls fn_admin_checkin_prepare via /booking-checkin (mode='prepare')
//      to identify the booking and load eligible earlier slots.
//   2. If the booking is in the future and there's a same-room earlier slot
//      with capacity, render a vertical timeline picker (rows top→bottom =
//      earlier→booked, dashed "Nu" rule between the rows that bracket now).
//   3. If the operator picks one, move the booking via /booking-edit
//      (action='slots', single leg, no force, reason='early_arrival_bump').
//   4. Run the actual check-in via /booking-checkin (mode='checkin') —
//      idempotent, mutates lifecycle to 'arrived'. The play language the
//      operator confirmed in the picker (chips, prefilled from the booking
//      locale) travels along and lands on departures.play_language.
//   5. Show a confirmation panel; operator dismisses with "Volgende". The
//      panel repeats the language chips; changing them saves immediately
//      via booking-queue set_language.
//
// The mutating slot edit must happen BEFORE the booking flips to 'arrived'
// (fn_admin_edit_booking_slots only allows 'confirmed' bookings — see 0045
// lines 78–82). That's why prepare+checkin are split across two calls.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  prepareCheckin,
  checkinBooking,
  checkinBookingById,
} from '../api/checkin';
import { setPlayLanguage } from '../api/queue';
import { editBooking } from '../api/edit-booking';
import { terminalQuote, type TerminalQuote } from '../api/terminal';
import { TerminalChargePanel } from './TerminalChargePanel';
import { GiftCardApplyPanel } from './GiftCardApplyPanel';
import { useAuth } from '../lib/auth';
import { beepError, beepSuccess, beepWarning } from '../lib/beep';
import { formatVenueDateTime, formatVenueTime } from '../lib/time';
import { useLocationFilter } from '../lib/location-context';
import {
  loadLanguageOptions,
  optionsFor,
  type PlayLanguageOption,
} from '../lib/play-languages';
import { LanguageChips, PlayLanguagePicker } from './PlayLanguagePicker';
import {
  type CheckinPrepareResponse,
  type CheckinResponse,
  type EarlierSlot,
} from '../types';

interface Props {
  qrToken?: string;
  bookingId?: string;
  // changed=true means the parent should re-fetch (booking transitioned to
  // arrived, or slot was moved). Caller decides what to do (scan: resume
  // camera; manual: refresh BookingDetail / DepartureModal).
  onClose: (changed: boolean) => void;
}

type State =
  | { kind: 'loading' }
  | {
      kind: 'picker';
      prep: CheckinPrepareResponse;
      pickedSlot: EarlierSlot | null;
    }
  | { kind: 'checking-in' }
  // Unpaid departure + the location has a WisePOS: offer "pay on the
  // terminal and check in" before the plain check-in.
  | { kind: 'pay-offer'; prep: CheckinPrepareResponse; bumped: boolean; quote: TerminalQuote }
  | { kind: 'paying'; prep: CheckinPrepareResponse; bumped: boolean }
  // Spend a physical gift card on the amount due, then pay the rest or check in.
  | { kind: 'giftcard'; prep: CheckinPrepareResponse; bumped: boolean; quote: TerminalQuote }
  | { kind: 'done'; data: CheckinResponse; bumped: boolean; paidNow: boolean }
  | { kind: 'error'; message: string };

function paymentLabel(s: string | undefined): string {
  switch (s) {
    case 'pending':  return 'In behandeling';
    case 'failed':   return 'Mislukt';
    case 'refunded': return 'Terugbetaald';
    case 'unpaid':   return 'Nog niet betaald';
    case 'paid':     return 'Betaald';
    case 'none':     return 'Geen';
    default:         return s ?? 'onbekend';
  }
}

function isUnpaid(s: string | undefined): boolean {
  return s === 'pending' || s === 'failed' || s === 'refunded' || s === 'unpaid';
}

// -----------------------------------------------------------------------------
export function CheckinFlowModal({ qrToken, bookingId, onClose }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  // Terminal quote for an unpaid departure, fetched alongside prepare. null =
  // not applicable (paid, no reader, not payable) or lookup failed.
  const [quote, setQuote] = useState<TerminalQuote | null>(null);
  const { can } = useAuth();
  const canSellGiftCards = can('booking_gift_card_sell');
  // Play language shown in the picker chips. Prefilled from prepare
  // (confirmed value, else the locale default) once the picker is shown; the
  // ref mirrors the state so the check-in callbacks below read the latest
  // pick without re-creating themselves. Stays null on the skip-picker
  // path, so nothing the operator never saw gets stamped as confirmed.
  const [playLanguage, setPlayLanguageState] = useState<string | null>(null);
  // Experience of the departure, captured from prepare. The done view's
  // response doesn't carry it, and the language chips need it to know which
  // languages this experience actually supports.
  const [experienceId, setExperienceId] = useState<string | null>(null);
  // Per-experience language options for this location, keyed by experience_id.
  // Session-cached in lib/play-languages; null until the first load lands, at
  // which point optionsFor() falls back to NL/EN so the picker is never empty.
  const { locationId } = useLocationFilter();
  const [langOptions, setLangOptions] =
    useState<Record<string, PlayLanguageOption[]> | null>(null);
  const playLanguageRef = useRef<string | null>(null);
  const choosePlayLanguage = useCallback((code: string | null) => {
    playLanguageRef.current = code;
    setPlayLanguageState(code);
  }, []);

  // Plain check-in call + done state. `paidNow` marks a check-in that
  // followed a terminal payment in this same flow.
  const doCheckin = useCallback(
    async (prep: CheckinPrepareResponse, bumped: boolean, paidNow: boolean): Promise<void> => {
      setState({ kind: 'checking-in' });
      const lang = playLanguageRef.current ?? undefined;
      const ck = qrToken && !paidNow
        ? await checkinBooking(qrToken, lang)
        : await checkinBookingById(paidNow ? prep.departure_id : bookingId!, lang);
      if (!ck.ok || !ck.data) {
        beepError();
        setState({
          kind: 'error',
          message:
            ck.error?.message ??
            ck.error?.error ??
            'Check-in mislukt',
        });
        return;
      }
      if (ck.data.already_checked_in && !paidNow) beepWarning();
      else beepSuccess();
      setState({ kind: 'done', data: ck.data, bumped, paidNow });
    },
    [qrToken, bookingId],
  );

  // Run the actual check-in (with optional pre-bump). Used by both the
  // skip-picker auto-path and the operator's confirm action. When the
  // departure is unpaid and the location has a terminal, the flow detours
  // via the pay-offer first (after the bump, which needs 'confirmed').
  const runCheckin = useCallback(
    async (
      prep: CheckinPrepareResponse,
      target: EarlierSlot | null,
      skipPay = false,
    ): Promise<void> => {
      setState({ kind: 'checking-in' });

      if (target) {
        const moveRes = await editBooking({
          action: 'slots',
          // booking-edit operates per-departure. prep.booking_id is the
          // parent purchase id (different from departure.id post-EXPAND);
          // pass departure_id explicitly.
          booking_id: prep.departure_id,
          new_party_size: prep.party_size,
          new_slots: [
            {
              room_id:    target.room_id,
              slot_start: target.slot_start,
              party_size: prep.party_size,
            },
          ],
          force: false,
          reason: 'early_arrival_bump',
        });
        if (!moveRes.ok) {
          beepError();
          setState({
            kind: 'error',
            message:
              moveRes.error?.message ??
              moveRes.error?.error ??
              'Tijdslot verplaatsen mislukt',
          });
          return;
        }
      }

      const terminalOffer =
        !skipPay &&
        quote !== null &&
        quote.payable &&
        quote.reader.configured &&
        prep.lifecycle_status === 'confirmed' &&
        isUnpaid(prep.payment_status);
      if (terminalOffer) {
        beepWarning();
        setState({ kind: 'pay-offer', prep, bumped: target != null, quote });
        return;
      }

      await doCheckin(prep, target != null, false);
    },
    [quote, doCheckin],
  );

  // Mount: prepare lookup, then either render picker or skip straight to check-in.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await prepareCheckin({
        qr_token: qrToken,
        booking_id: bookingId,
      });
      if (cancelled) return;
      if (!res.ok || !res.data) {
        beepError();
        setState({
          kind: 'error',
          message:
            res.error?.message ??
            res.error?.error ??
            'Onbekende fout',
        });
        return;
      }
      const prep = res.data;
      setExperienceId(prep.experience_id ?? null);
      // Unpaid + still confirmed → ask the terminal side whether a POS charge
      // is possible (amount due + reader configured/online). Best-effort.
      if (isUnpaid(prep.payment_status) && prep.lifecycle_status === 'confirmed') {
        const q = await terminalQuote(prep.booking_id);
        if (cancelled) return;
        setQuote(q.ok && q.data ? q.data : null);
      }
      // Show the picker whenever the booking is still confirmed AND its slot
      // hasn't passed yet — even if eligible_earlier_slots is empty. The
      // operator should always get the chance to confirm/skip rather than
      // being silently fast-forwarded through to "checked in". If the slot
      // is already in the past (or lifecycle isn't confirmed), skip.
      const slotStartMs = prep.slot_start ? new Date(prep.slot_start).getTime() : null;
      const slotInFuture = slotStartMs != null && slotStartMs > Date.now();
      const showPicker =
        prep.lifecycle_status === 'confirmed' && slotInFuture;
      if (!showPicker) {
        await runCheckin(prep, null);
      } else {
        choosePlayLanguage(prep.play_language ?? prep.play_language_effective ?? null);
        setState({ kind: 'picker', prep, pickedSlot: null });
      }
    })();
    return () => {
      cancelled = true;
    };
    // runCheckin depends on `quote`, which this effect itself sets; re-running
    // on quote changes would double-prepare. Mount-only on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrToken, bookingId]);

  // Language options for the venue. One fetch per location per session (the
  // list only moves when RealityStudio enables a language), so this is a
  // no-op on every modal after the first.
  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    loadLanguageOptions(locationId)
      .then((opts) => {
        if (!cancelled) setLangOptions(opts);
      })
      .catch(() => {
        // Non-fatal: optionsFor() falls back to NL/EN and check-in proceeds.
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  // Escape closes whenever it's safe (any state except mid-network-call).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (state.kind === 'loading' || state.kind === 'checking-in' || state.kind === 'paying') return;
      // For 'done' we treat Escape == Volgende: parent should reload.
      onClose(state.kind === 'done');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.kind, onClose]);

  return (
    <div className="dep-modal-backdrop" onClick={() => { /* close via ✕ / Esc / button only */ }}>
      <div
        className="dep-modal checkin-flow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {state.kind === 'loading' || state.kind === 'checking-in' ? (
          <div className="dep-modal-body checkin-flow-busy">
            <div className="muted">
              {state.kind === 'loading' ? 'Boeking ophalen…' : 'Inchecken…'}
            </div>
          </div>
        ) : null}

        {state.kind === 'picker' ? (
          <PickerView
            prep={state.prep}
            pickedSlot={state.pickedSlot}
            playLanguage={playLanguage}
            onPlayLanguage={choosePlayLanguage}
            langOptions={langOptions}
            onPick={(s) =>
              setState((prev) =>
                prev.kind === 'picker'
                  ? { ...prev, pickedSlot: s }
                  : prev,
              )
            }
            onConfirm={() => void runCheckin(state.prep, state.pickedSlot)}
            onSkip={() => void runCheckin(state.prep, null)}
            onCancel={() => onClose(false)}
          />
        ) : null}

        {state.kind === 'pay-offer' ? (
          <PayOfferView
            prep={state.prep}
            quote={state.quote}
            onPay={() => setState({ kind: 'paying', prep: state.prep, bumped: state.bumped })}
            onSkip={() => void doCheckin(state.prep, state.bumped, false)}
            onCancel={() => onClose(state.bumped)}
            onGiftCard={
              canSellGiftCards
                ? () => setState({ kind: 'giftcard', prep: state.prep, bumped: state.bumped, quote: state.quote })
                : undefined
            }
          />
        ) : null}

        {state.kind === 'giftcard' ? (
          <>
            <header className="dep-modal-head">
              <div>
                <h2>Betalen met cadeaubon</h2>
                <div className="dep-modal-sub muted">{state.prep.booking_number}</div>
              </div>
            </header>
            <div className="dep-modal-body">
              <GiftCardApplyPanel
                bookingId={state.prep.booking_id}
                onCancel={() => setState({ kind: 'pay-offer', prep: state.prep, bumped: state.bumped, quote: state.quote })}
                onApplied={(res) => {
                  const { prep, bumped } = state;
                  if (res.fully_paid) {
                    void doCheckin(prep, bumped, true);
                    return;
                  }
                  // Rest on the terminal: refresh the amount due first.
                  void (async () => {
                    const q = await terminalQuote(prep.booking_id);
                    const nextQuote = q.ok && q.data ? q.data : state.quote;
                    setQuote(nextQuote);
                    setState({ kind: 'pay-offer', prep, bumped, quote: nextQuote });
                  })();
                }}
              />
            </div>
          </>
        ) : null}

        {state.kind === 'paying' ? (
          <>
            <header className="dep-modal-head">
              <div>
                <h2>Afrekenen en inchecken</h2>
                <div className="dep-modal-sub muted">{state.prep.booking_number}</div>
              </div>
            </header>
            <div className="dep-modal-body">
              <TerminalChargePanel
                bookingId={state.prep.booking_id}
                bookingNumber={state.prep.booking_number}
                checkinDepartureId={state.prep.departure_id}
                onSettled={() => void doCheckin(state.prep, state.bumped, true)}
                onAbandon={() => setState({ kind: 'pay-offer', prep: state.prep, bumped: state.bumped, quote: quote ?? { booking_number: state.prep.booking_number, amount_due_cents: 0, currency: 'eur', payment_status: state.prep.payment_status, lifecycle_status: state.prep.lifecycle_status, payable: true, reader: { configured: true, id: null, label: null, status: null, busy: false } } })}
              />
            </div>
          </>
        ) : null}

        {state.kind === 'done' ? (
          <DoneView
            data={state.data}
            bumped={state.bumped}
            paidNow={state.paidNow}
            langOptions={langOptions}
            experienceId={experienceId}
            onClose={() => onClose(true)}
          />
        ) : null}

        {state.kind === 'error' ? (
          <ErrorView message={state.message} onClose={() => onClose(false)} />
        ) : null}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PickerView — vertical timeline of eligible earlier slots + the booked slot.
// "Nu" line is rendered as its own row positioned between the slots whose
// timestamps bracket the current moment.
// -----------------------------------------------------------------------------

type TimelineRow =
  | { kind: 'slot'; slot: EarlierSlot }
  | { kind: 'now'; t: Date }
  | { kind: 'booked' };

function PickerView({
  prep,
  pickedSlot,
  playLanguage,
  onPlayLanguage,
  langOptions,
  onPick,
  onConfirm,
  onSkip,
  onCancel,
}: {
  prep: CheckinPrepareResponse;
  pickedSlot: EarlierSlot | null;
  playLanguage: string | null;
  onPlayLanguage: (code: string) => void;
  langOptions: Record<string, PlayLanguageOption[]> | null;
  onPick: (s: EarlierSlot) => void;
  onConfirm: () => void;
  onSkip: () => void;
  onCancel: () => void;
}) {
  // Snapshot now once at mount — modal isn't long-lived enough to need a ticker.
  const now = useMemo(() => new Date(), []);
  const bookedAt = prep.slot_start ? new Date(prep.slot_start) : null;

  const timeUntilLabel = useMemo(() => {
    if (!bookedAt) return null;
    const diffMin = Math.max(
      0,
      Math.round((bookedAt.getTime() - now.getTime()) / 60000),
    );
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h}u`;
    return `${h}u ${m}min`;
  }, [bookedAt, now]);

  const rows: TimelineRow[] = useMemo(() => {
    const out: TimelineRow[] = [];
    let nowInserted = false;
    for (const s of prep.eligible_earlier_slots) {
      if (!nowInserted && new Date(s.slot_start).getTime() > now.getTime()) {
        out.push({ kind: 'now', t: now });
        nowInserted = true;
      }
      out.push({ kind: 'slot', slot: s });
    }
    if (!nowInserted) out.push({ kind: 'now', t: now });
    out.push({ kind: 'booked' });
    return out;
  }, [prep.eligible_earlier_slots, now]);

  const bookedTimeStr = prep.slot_start ? formatVenueTime(prep.slot_start) : '—';
  const nowTimeStr = formatVenueTime(now.toISOString());

  // The "(nog niet bevestigd)" hint shows while the chips only echo the
  // locale default; it disappears once the DB has a confirmed value or the
  // operator taps a different chip (the check-in then stamps the pick).
  const prefilledLanguage = prep.play_language ?? prep.play_language_effective ?? null;
  const langConfirmedInPicker =
    prep.play_language_confirmed === true || playLanguage !== prefilledLanguage;

  return (
    <>
      <header className="dep-modal-head">
        <div>
          <h2>Vroeger inchecken?</h2>
          <div className="dep-modal-sub muted">
            {prep.booking_number}
            {prep.customer_name ? ` · ${prep.customer_name}` : ''}
            {' · '}
            {prep.party_size}p
          </div>
        </div>
        <button
          className="btn-link dep-modal-close"
          onClick={onCancel}
          aria-label="Sluiten"
        >
          ✕
        </button>
      </header>

      <div className="dep-modal-body">
        <p className="checkin-flow-lede muted">
          Klant is geboekt om <strong>{bookedTimeStr}</strong>
          {timeUntilLabel ? <> · nog {timeUntilLabel}</> : null}.
          {prep.eligible_earlier_slots.length > 0
            ? ' Kies een eerder slot om ze nu in te checken, of houd het origineel.'
            : ' Geen eerdere geschikte tijdsloten gevonden — check direct in om de geboekte tijd.'}
        </p>

        <ul className="checkin-flow-timeline">
          {rows.map((r, idx) => {
            if (r.kind === 'now') {
              return (
                <li
                  key={`now-${idx}`}
                  className="checkin-flow-now-line"
                  aria-hidden="true"
                >
                  <span className="checkin-flow-now-label">Nu · {nowTimeStr}</span>
                </li>
              );
            }
            if (r.kind === 'booked') {
              return (
                <li key="booked" className="checkin-flow-row checkin-flow-booked-row">
                  <span className="checkin-flow-time">{bookedTimeStr}</span>
                  <span className="checkin-flow-content">
                    <span className="checkin-flow-star">★</span>
                    <span>GEBOEKT (oorspronkelijk)</span>
                  </span>
                </li>
              );
            }
            const s = r.slot;
            const isPicked =
              pickedSlot != null &&
              pickedSlot.slot_start === s.slot_start &&
              pickedSlot.room_id === s.room_id;
            const remaining = Math.max(0, s.capacity - s.booked);
            return (
              <li key={`slot-${s.slot_start}-${s.room_id}`}>
                <button
                  type="button"
                  className={`checkin-flow-row checkin-flow-row-pick ${
                    isPicked ? 'checkin-flow-row-picked' : ''
                  }`}
                  onClick={() => onPick(s)}
                >
                  <span className="checkin-flow-time">{s.time_of_day}</span>
                  <span className="checkin-flow-content">
                    <span
                      className={`checkin-flow-pill ${
                        s.currently_running ? 'checkin-flow-pill-running' : ''
                      }`}
                    >
                      {s.currently_running ? 'Nu bezig' : 'Aanstaand'}
                    </span>
                    <span className="checkin-flow-cap muted">
                      · {remaining}/{s.capacity} vrij
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="checkin-flow-lang">
          <div className="checkin-flow-lang-head">
            <span className="checkin-flow-lang-label">Speeltaal</span>
            <span className="checkin-flow-lang-mode muted">· iedereen</span>
          </div>
          {/* Pre-check-in there are no queue rows yet, so there is nothing to
              split per person; that option appears on the confirmation. */}
          <LanguageChips
            value={playLanguage}
            options={optionsFor(langOptions, prep.experience_id, playLanguage)}
            onChange={onPlayLanguage}
          />
          {!langConfirmedInPicker ? (
            <span className="checkin-flow-lang-hint muted">(nog niet bevestigd)</span>
          ) : null}
        </div>

        <div className="walkin-step-actions checkin-flow-actions">
          <button className="checkin-flow-skip-btn" onClick={onSkip}>
            Origineel houden — om {bookedTimeStr}
          </button>
          <button
            className="checkin-flow-confirm-btn"
            disabled={pickedSlot == null}
            onClick={onConfirm}
          >
            {pickedSlot
              ? `Inchecken om ${pickedSlot.time_of_day}`
              : 'Kies een eerder tijdslot'}
          </button>
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// DoneView — confirmation panel, replaces the old auto-resume scan-result.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// PayOfferView — unpaid departure, location has a WisePOS: pay + check in.
// -----------------------------------------------------------------------------

function PayOfferView({
  prep,
  quote,
  onPay,
  onSkip,
  onCancel,
  onGiftCard,
}: {
  prep: CheckinPrepareResponse;
  quote: TerminalQuote;
  onPay: () => void;
  onSkip: () => void;
  onCancel: () => void;
  onGiftCard?: () => void;
}) {
  const eur = (quote.amount_due_cents / 100).toFixed(2);
  const readerOffline = quote.reader.status === 'offline' || quote.reader.status === 'missing';
  return (
    <>
      <header className="dep-modal-head">
        <div>
          <h2>⚠ Nog niet betaald</h2>
          <div className="dep-modal-sub muted">{prep.booking_number}</div>
        </div>
      </header>
      <div className="dep-modal-body">
        <div className="scan-result scan-result-warn">
          <div className="scan-result-line">
            <strong>{prep.booking_number}</strong>
            {prep.customer_name ? ` · ${prep.customer_name}` : ''}
          </div>
          <div className="scan-result-line muted">
            {prep.experience_name ?? '—'} · {prep.party_size}p
            {prep.slot_start ? ` · ${formatVenueDateTime(prep.slot_start)}` : ''}
          </div>
          <div className="scan-result-line">
            Openstaand: <strong>€ {eur}</strong>
            {' · '}
            <span className="muted">{paymentLabel(prep.payment_status)}</span>
          </div>
        </div>

        {quote.reader.busy ? (
          <div className="scan-payment-info">
            De pinterminal is bezet met een andere betaling — probeer zo opnieuw.
          </div>
        ) : readerOffline ? (
          <div className="scan-payment-info">
            {quote.reader.status === 'missing'
              ? `De pinterminal (${quote.reader.label ?? quote.reader.id}) bestaat niet meer in Stripe — kies een andere bij Setup → Locaties.`
              : `De pinterminal (${quote.reader.label ?? quote.reader.id}) is offline (uit, of geen internet).`}
          </div>
        ) : null}

        <div className="walkin-step-actions">
          <button className="btn-link" onClick={onCancel}>Annuleren</button>
          <button className="btn-link" onClick={onSkip}>
            Toch inchecken zonder betaling
          </button>
          {onGiftCard ? (
            <button className="btn-secondary" onClick={onGiftCard}>
              Cadeaubon
            </button>
          ) : null}
          <button
            className="walkin-primary-btn"
            onClick={onPay}
            disabled={quote.reader.busy || readerOffline}
            autoFocus
          >
            Afrekenen op pinterminal (€ {eur}) en inchecken
          </button>
        </div>
      </div>
    </>
  );
}

function DoneView({
  data,
  bumped,
  paidNow,
  langOptions,
  experienceId,
  onClose,
}: {
  data: CheckinResponse;
  bumped: boolean;
  paidNow: boolean;
  langOptions: Record<string, PlayLanguageOption[]> | null;
  /** From the prepare response — the check-in response doesn't carry it. */
  experienceId: string | null;
  onClose: () => void;
}) {
  // PlayLanguagePicker owns the language block from here: it has to track group
  // vs per-person against the DB, which is more state than a chip row. These two
  // only prefill it so the chips render before its first fetch lands.
  const lang = data.play_language ?? data.play_language_effective ?? null;
  const langConfirmed = data.play_language_confirmed === true;

  return (
    <>
      <header className="dep-modal-head">
        <div>
          <h2>
            {paidNow
              ? '✓ Betaald & ingecheckt'
              : data.already_checked_in ? '↻ Al ingecheckt' : '✓ Ingecheckt'}
          </h2>
          <div className="dep-modal-sub muted">{data.booking_number}</div>
        </div>
      </header>
      <div className="dep-modal-body">
        <div
          className={`scan-result ${
            data.already_checked_in ? 'scan-result-warn' : 'scan-result-ok'
          }`}
        >
          <div className="scan-result-line">
            <strong>{data.booking_number}</strong>
            {data.customer_name ? ` · ${data.customer_name}` : ''}
          </div>
          <div className="scan-result-line muted">
            {data.experience_name ?? '—'}
            {' · '}
            {data.party_size}p
            {data.room_name ? ` · ${data.room_name}` : ''}
          </div>
          {data.slot_start ? (
            <div className="scan-result-line muted">
              {formatVenueDateTime(data.slot_start)}
              {bumped ? <span className="checkin-flow-bumped"> · vroeger ingecheckt</span> : null}
            </div>
          ) : null}
          {data.departure_id ? (
            <PlayLanguagePicker
              departureId={data.departure_id}
              experienceId={experienceId}
              langOptions={langOptions}
              initialLanguage={lang}
              initialConfirmed={langConfirmed}
            />
          ) : null}
        </div>

        {paidNow ? (
          <div className="scan-payment-info">
            Betaald op de pinterminal ✓ — ticket wordt gemaild.
          </div>
        ) : isUnpaid(data.payment_status) ? (
          <div className="scan-payment-warn">
            <div className="scan-payment-warn-title">⚠ NIET BETAALD</div>
            <div className="scan-payment-warn-status">
              Status: {paymentLabel(data.payment_status)}
            </div>
            <div className="scan-payment-warn-hint">
              Vraag de klant om alsnog te betalen voordat ze de
              experience betreden.
            </div>
          </div>
        ) : data.payment_status === 'none' ? (
          <div className="scan-payment-info">
            Geen betaling geregistreerd (comp / gratis).
          </div>
        ) : null}

        <div className="walkin-step-actions">
          <button className="walkin-primary-btn" onClick={onClose} autoFocus>
            Volgende
          </button>
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// ErrorView — surface RPC / network errors with a Sluiten button.
// -----------------------------------------------------------------------------

function ErrorView({
  message,
  onClose,
}: {
  message: string;
  onClose: () => void;
}) {
  return (
    <>
      <header className="dep-modal-head">
        <div>
          <h2>✗ Mislukt</h2>
        </div>
      </header>
      <div className="dep-modal-body">
        <div className="error-card">{message}</div>
        <div className="walkin-step-actions">
          <button className="walkin-primary-btn" onClick={onClose} autoFocus>
            Sluiten
          </button>
        </div>
      </div>
    </>
  );
}
